import { TextProps, List } from '@chakra-ui/react';
import type { Element, ElementContent, Parent } from 'hast';
import { toText } from 'hast-util-to-text';
import isEqual from 'lodash/isEqual';
import Papa from 'papaparse';
import React, {
  createContext,
  Dispatch,
  FC,
  memo,
  SetStateAction,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Components, Options } from 'react-markdown';
import { Link as ReactDomLink } from 'react-router-dom';
import { logger } from '../../services/logger';
import lazyWithPreload from '../App/LazyLoadRetry';
import {
  MCode,
  MDivider,
  MLink,
  MListItem,
  MSpinner,
  MTable,
  MTbody,
  MTd,
  MText,
  MTh,
  MThead,
  MTr,
  MUnorderedList,
} from '../Monetize';
import { rehypeLinkifyRecordIds } from './recordLinks';

interface MarkdownRendererProps {
  text: string;
  textProps?: TextProps;
  setTablesCsv: Dispatch<SetStateAction<string[]>>;
}

const ReactMarkdownLazy = lazyWithPreload(() => import('react-markdown'));

type MarkdownPlugin = NonNullable<Options['remarkPlugins']>[number];

interface MarkdownPlugins {
  remarkGfm: MarkdownPlugin;
  rehypeRaw: MarkdownPlugin;
  rehypeSanitize: MarkdownPlugin;
}

// Loaded once and shared: a message that mounts after the first one renders
// immediately instead of flashing a spinner (and shifting the layout) while
// it re-awaits chunks that are already in memory.
let loadedPlugins: MarkdownPlugins | null = null;
let pluginsPromise: Promise<MarkdownPlugins> | null = null;

const loadMarkdownPlugins = (): Promise<MarkdownPlugins> => {
  pluginsPromise ??= Promise.all([
    import('remark-gfm'),
    import('rehype-raw'),
    import('rehype-sanitize'),
  ])
    .then(([gfm, raw, sanitize]) => {
      loadedPlugins = {
        remarkGfm: gfm.default,
        rehypeRaw: raw.default,
        rehypeSanitize: sanitize.default,
      };
      return loadedPlugins;
    })
    .catch((error) => {
      // A failed chunk load must not wedge every future message.
      pluginsPromise = null;
      throw error;
    });
  return pluginsPromise;
};

const fixAiMarkdown = (raw: string): string => {
  const lines = raw.split('\n');
  const fixed: string[] = [];
  let insideList = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (/^\d+\.\s/.test(line)) {
      fixed.push(line);
      insideList = true;
      continue;
    }
    if (/^[-*]\s/.test(line) && insideList) {
      fixed.push(`\t${line}`);
      continue;
    }
    if (/^\s*$/.test(line) || /^[^\s]/.test(line)) {
      insideList = false;
    }
    fixed.push(line);
  }

  return fixed.join('\n');
};

const hastTableToCsv = (hastNode: Element) => {
  const rows: string[][] = [];

  const walk = (node: ElementContent) => {
    if (node.type === 'element' && node.tagName === 'tr') {
      const cells = (node.children || [])
        .filter(
          (child): child is Element =>
            child.type === 'element' &&
            (child.tagName === 'td' || child.tagName === 'th'),
        )
        .map((cell) => toText(cell).trim());

      if (cells.length > 0) {
        rows.push(cells);
      }
    }

    if ('children' in node && Array.isArray(node.children)) {
      for (const k of node.children) {
        walk(k);
      }
    }
  };

  walk(hastNode);

  // If there are no rows or all cells are empty, treat as "no table"
  const hasContent = rows.some((row) => row.some((cell) => cell.length > 0));
  if (!hasContent) {
    return null;
  }

  return Papa.unparse(rows, { quotes: true, newline: '\n' });
};

const rehypeCollectTables = (targetRef: React.MutableRefObject<string[]>) => {
  return () => {
    return (tree: Parent) => {
      const tables: string[] = [];

      const visit = (node) => {
        if (node?.type === 'element' && node?.tagName === 'table') {
          const csv = hastTableToCsv(node as Element);
          if (csv !== null) {
            tables.push(csv);
          }
        }
        const kids = node?.children;
        if (Array.isArray(kids)) {
          for (const k of kids) {
            visit(k);
          }
        }
      };

      visit(tree);
      targetRef.current = tables;
    };
  };
};

interface TablesCsvSyncProps {
  collectedTablesRef: React.MutableRefObject<string[]>;
  setTablesCsv: Dispatch<SetStateAction<string[]>>;
}

// Rendered after ReactMarkdown inside the same Suspense boundary, so its effect
// runs after every markdown render -- including the first one once the lazy
// plugins and react-markdown chunk load. An effect keyed on `text` misses that
// render, so a message mounted with its final text (e.g. on reopening the
// panel) never reported its tables.
const TablesCsvSync: FC<TablesCsvSyncProps> = ({
  collectedTablesRef,
  setTablesCsv,
}) => {
  const committedTablesRef = useRef<string[]>([]);

  useEffect(() => {
    const next = collectedTablesRef.current;

    if (!isEqual(committedTablesRef.current, next)) {
      committedTablesRef.current = next;
      setTablesCsv(next);
    }
  });

  return null;
};

// True inside a markdown link, so content there doesn't add an anchor of its
// own: nested anchors are invalid, and the outer link would swallow the click.
const InsideLinkContext = createContext(false);

interface MarkdownImageProps {
  src?: string;
  alt?: string;
}

// Never auto-load remote images: text written by customers or synced from a
// CRM can reach the model, and an image URL in its answer would send the
// request (and anything encoded in it) to a third party with no click. Show a
// link instead; opening it is the user's choice. A linked image is just text
// for its surrounding link.
const MarkdownImage: FC<MarkdownImageProps> = ({ src, alt }) => {
  const isInsideLink = useContext(InsideLinkContext);
  if (!src) {
    return null;
  }

  const label = alt ? `${alt} (image)` : 'View image';
  if (isInsideLink) {
    return label;
  }
  return (
    <MLink
      href={src}
      target="_blank"
      rel="noopener noreferrer"
      mb={2}
      color="fg.link"
      whiteSpace="pre-wrap"
      fontSize="14px"
    >
      {label}
    </MLink>
  );
};

const MarkdownRendererComponent: FC<MarkdownRendererProps> = ({
  text,
  textProps,
  setTablesCsv,
}) => {
  const [plugins, setPlugins] = useState<MarkdownPlugins | null>(loadedPlugins);

  const collectedTablesRef = useRef<string[]>([]);

  useEffect(() => {
    if (plugins) {
      return;
    }
    let mounted = true;
    loadMarkdownPlugins()
      .then((loaded) => mounted && setPlugins(loaded))
      .catch((error) =>
        logger.error('MarkdownRenderer: failed to load plugins', error),
      );
    return () => {
      mounted = false;
    };
  }, [plugins]);

  const collectTablesPlugin = useMemo(
    () => rehypeCollectTables(collectedTablesRef),
    [],
  );

  const components = useMemo(
    () => ({
      h1: ({ children }) => (
        <MText as="h1" fontSize="2xl" fontWeight="bold" mb={3} {...textProps}>
          {children}
        </MText>
      ),
      h2: ({ children }) => (
        <MText
          as="h2"
          fontSize="xl"
          fontWeight="semibold"
          mb={3}
          {...textProps}
        >
          {children}
        </MText>
      ),
      h3: ({ children }) => (
        <MText
          as="h3"
          fontSize="lg"
          fontWeight="semibold"
          mb={2}
          {...textProps}
        >
          {children}
        </MText>
      ),
      h4: ({ children }) => (
        <MText
          as="h4"
          fontSize="md"
          fontWeight="semibold"
          mb={2}
          {...textProps}
        >
          {children}
        </MText>
      ),
      h5: ({ children }) => (
        <MText
          as="h5"
          fontSize="sm"
          fontWeight="semibold"
          mb={2}
          {...textProps}
        >
          {children}
        </MText>
      ),
      h6: ({ children }) => (
        <MText
          as="h6"
          fontSize="xs"
          fontWeight="semibold"
          mb={2}
          {...textProps}
        >
          {children}
        </MText>
      ),
      table: ({ children }) => (
        <MTable
          size="sm"
          mb={4}
          overflowX="auto"
          maxW="100%"
          display="block"
          width="max-content"
        >
          {children}
        </MTable>
      ),
      thead: ({ children }) => <MThead>{children}</MThead>,
      tbody: ({ children }) => <MTbody>{children}</MTbody>,
      tr: ({ children }) => <MTr>{children}</MTr>,
      th: ({ children }) => (
        <MTh fontWeight="bold" fontSize="sm" color="gray.700">
          {children}
        </MTh>
      ),
      td: ({ children }) => (
        <MTd fontSize="sm" color="gray.600">
          {children}
        </MTd>
      ),
      p: ({ children }) => (
        <MText mb={2} whiteSpace="pre-wrap" {...textProps}>
          {children}
        </MText>
      ),
      strong: ({ children }) => (
        <MText
          as="strong"
          fontWeight="bold"
          display="inline"
          mb={2}
          {...textProps}
        >
          {children}
        </MText>
      ),
      em: ({ children }) => (
        <MText
          as="em"
          fontStyle="italic"
          display="inline"
          mb={2}
          {...textProps}
        >
          {children}
        </MText>
      ),
      ul: ({ children }) => (
        <MUnorderedList mb={2} color={textProps?.color} fontSize="14px">
          {children}
        </MUnorderedList>
      ),
      ol: ({ children, node }) => (
        <List.Root asChild mb={2} color={textProps?.color} fontSize="14px">
          <ol start={Number(node?.properties?.start) || 1}>{children}</ol>
        </List.Root>
      ),
      li: ({ children }) => {
        const hasNestedList = React.Children.toArray(children)
          .filter((child) => React.isValidElement(child))
          .some(
            (child) =>
              child?.props?.node?.tagName === 'ul' ||
              child?.props?.node?.tagName === 'ol',
          );

        return (
          <MListItem
            mb={hasNestedList ? 2 : 0}
            color={textProps?.color}
            fontSize="14px"
          >
            {children}
          </MListItem>
        );
      },
      a: ({ children, href }) => {
        const linkContent = (
          <InsideLinkContext.Provider value>
            {children}
          </InsideLinkContext.Provider>
        );
        if (href?.startsWith('/')) {
          return (
            <MLink
              asChild
              mb={2}
              color="fg.link"
              fontWeight={500}
              whiteSpace="pre-wrap"
              fontSize="14px"
            >
              <ReactDomLink to={href}>{linkContent}</ReactDomLink>
            </MLink>
          );
        }
        return (
          <MLink
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            mb={2}
            color="fg.link"
            whiteSpace="pre-wrap"
            fontSize="14px"
          >
            {linkContent}
          </MLink>
        );
      },
      code: ({ children }) => (
        <MCode mb={2} fontSize="14px" {...textProps}>
          {children}
        </MCode>
      ),
      img: MarkdownImage,
      hr: () => <MDivider mb={2} />,
    }),
    [textProps],
  );

  if (!plugins) {
    return <MSpinner size="sm" />;
  }

  return (
    <Suspense fallback={<MSpinner size="sm" />}>
      <ReactMarkdownLazy
        remarkPlugins={[plugins.remarkGfm]}
        // Raw HTML is parsed, then sanitized before anything else touches the
        // tree: model output is untrusted, and the default schema drops
        // scripts, iframes, styles, forms, event handlers and `javascript:`
        // URLs while keeping standard formatting tags.
        rehypePlugins={[
          plugins.rehypeRaw,
          plugins.rehypeSanitize,
          rehypeLinkifyRecordIds,
          collectTablesPlugin,
        ]}
        components={components as Components}
      >
        {fixAiMarkdown(text)}
      </ReactMarkdownLazy>
      <TablesCsvSync
        collectedTablesRef={collectedTablesRef}
        setTablesCsv={setTablesCsv}
      />
    </Suspense>
  );
};

// Only re-parses when the text changes: a conversation's settled messages must
// not be re-processed on every token streamed into the newest one.
export const MarkdownRenderer = memo(MarkdownRendererComponent);
